import type { Chat, ChatAttachment, ChatEvent } from "@codvero/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { ArrowUp, Check, ChevronDown, Combine, FolderOpen, MessageSquareDashed, Pencil, Plus, Square, Users, X } from "lucide-react";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { accountsApi } from "@/api/accounts.ts";
import { filesApi } from "@/api/browser.ts";
import { chatsApi } from "@/api/chats.ts";
import { spacesApi } from "@/api/spaces.ts";
import { MessageList } from "@/features/chat/MessageList.tsx";
import { useChatStream } from "@/features/chat/useChatStream.ts";
import { captureBus, ChatHeaderToolsContext } from "@/features/projects/ProjectPage.tsx";
import { cn } from "@/lib/cn.ts";
import { softKeyboardOpen } from "@/lib/keyboard.ts";
import { useDraft } from "@/lib/draft.ts";
import { useImageViewer } from "@/ui/ImageViewer.tsx";
import { Alert, Button, Spinner } from "@/ui/primitives.tsx";
import { ProviderIcon } from "@/ui/ProviderIcon.tsx";
import { useApiError } from "@/ui/useApiError.ts";
import { useConfirm } from "@/ui/ConfirmDialog.tsx";
import { accountOf, activityOf, MemberModel, memberHandles, providerName, useMultiChat } from "./providers.tsx";
import { ActivityChip } from "@/features/chat/TeamRoster.tsx";
import { WorkflowRunBar } from "./Workflows.tsx";
import { buildTimeline } from "./timeline.ts";
import { useMcpShortcuts } from "./McpShortcuts.tsx";
import { TemplatesButton } from "./Templates.tsx";
import { useTemporaryChat } from "./temporary.ts";

type Feed = { chat: Chat | null; events: ChatEvent[]; partial: string };

/** Keeps one member's live stream (one hook per member, so it lives in its own component) and reports it up. */
function MemberFeed({ chatId, onData }: { chatId: string; onData: (id: string, f: Feed) => void }) {
  const s = useChatStream(chatId);
  useEffect(() => { onData(chatId, { chat: s.chat, events: s.events, partial: s.partial }); }, [s.chat, s.events, s.partial]);
  return null;
}

/**
 * A conversation with several providers, laid out like a team chat: your message once, then each provider's reply
 * in turn (whoever finished first comes first), in the usual message design. The box at the bottom sends to everyone,
 * to the provider picked as recipient or @mentioned, or to the pinned one.
 */
export function MultiChatPage({ chatId }: { chatId: string }) {
  const { t } = useTranslation();
  const toMsg = useApiError();
  const qc = useQueryClient();
  const nav = useNavigate();
  const tools = useContext(ChatHeaderToolsContext);
  const viewImages = useImageViewer();
  const multi = useMultiChat(chatId);
  const accounts = useQuery({ queryKey: ["accounts"], queryFn: accountsApi.list, staleTime: 60_000 });
  const all = accounts.data ?? [];

  const [feeds, setFeeds] = useState<Record<string, Feed>>({});
  const onData = useMemo(() => (id: string, f: Feed) => setFeeds((v) => ({ ...v, [id]: f })), []);
  const live = (c: Chat) => feeds[c.id]?.chat ?? c;
  const members = (multi.data?.members ?? []).map(live);
  const combineChat = multi.data?.combine ? live(multi.data.combine) : null;
  const threads = [...members, ...(combineChat ? [combineChat] : [])];
  const turns = useMemo(() => buildTimeline(threads.map((m) => ({ member: m, events: feeds[m.id]?.events ?? [] }))), [feeds, multi.data]);
  const handles = memberHandles(members, all);
  const running = threads.filter((c) => c.status === "running");
  const temporary = !!multi.data?.chat.temporary;
  useTemporaryChat(chatId, temporary, running.length > 0);
  const pinnedMember = members.find((m) => !!multi.data?.chat.agentAccountId && m.agentAccountId === multi.data.chat.agentAccountId) ?? null;

  // recipients: an @mention wins, then the picked recipient, then the pinned provider, else everyone
  // kept on this device until sent: closing the page no longer loses what was typed
  const [text, setText] = useDraft(`chat:${chatId}`);
  const [to, setTo] = useState<string | null>(null);
  const [picker, setPicker] = useState(false);
  const [mentionQ, setMentionQ] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const mentioned = members.filter((m) => new RegExp(`(^|\\s)@${handles.get(m.id)}\\b`, "i").test(text));
  const recipients = mentioned.length ? mentioned : to ? members.filter((m) => m.id === to) : pinnedMember ? [pinnedMember] : members;
  const busy = recipients.some((m) => m.status === "running");
  const suggestions = mentionQ === null ? [] : members.filter((m) => handles.get(m.id)!.startsWith(mentionQ) || providerName(m, all).toLowerCase().includes(mentionQ));
  const insertMention = (handle: string) => { setText((v) => v.replace(/@([a-z0-9_]*)$/i, `@${handle} `)); setMentionQ(null); textareaRef.current?.focus(); };
  useEffect(() => {
    // the Providers panel addresses a provider by putting its @handle in the box
    const m = (e: Event) => { setText((v) => (v.trim() ? v.replace(/\s*$/, " ") : "") + `@${(e as CustomEvent<string>).detail} `); textareaRef.current?.focus(); };
    captureBus.addEventListener("mention", m);
    return () => captureBus.removeEventListener("mention", m);
  }, []);
  // opening the conversation clears the members' unread marks (the sidebar shows them on the conversation)
  useEffect(() => {
    for (const c of threads) if (c.unread && c.status !== "running") void chatsApi.update(c.id, { unread: false });
  }, [threads.map((c) => `${c.id}:${c.unread}:${c.status}`).join(",")]);

  const [attachments, setAttachments] = useState<ChatAttachment[]>([]);
  const [uploading, setUploading] = useState(0);
  const fileInput = useRef<HTMLInputElement>(null);
  const addFiles = async (files: FileList | File[]) => {
    for (const f of Array.from(files)) {
      setUploading((n) => n + 1);
      try {
        const att = await chatsApi.upload(chatId, f);
        setAttachments((v) => [...v, att]);
      } finally {
        setUploading((n) => n - 1);
      }
    }
  };
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["multi", chatId] });
    if (multi.data) void qc.invalidateQueries({ queryKey: ["chats", multi.data.chat.projectId, "list"] });
  };
  const send = useMutation({
    mutationFn: () => chatsApi.send(chatId, text.trim(), attachments, recipients.length === members.length ? undefined : recipients.map((m) => m.id)),
    onSuccess: () => { setText(""); setAttachments([]); refresh(); },
  });
  // which space the conversation belongs to (its instructions and files go to every provider)
  const spaces = useQuery({ queryKey: ["chat-spaces"], queryFn: spacesApi.list, staleTime: 60_000 });
  const { prompt } = useConfirm();
  // the pen beside the title renames the conversation (it used to start a new chat, which read as "edit" and lost the page)
  const rename = useMutation({ mutationFn: (title: string) => chatsApi.update(chatId, { title }), onSuccess: () => { refresh(); void qc.invalidateQueries({ queryKey: ["chats"] }); } });
  const askRename = async () => {
    const current = multi.data?.chat.title ?? "";
    const v = await prompt({ title: t("chatMenu.rename"), input: { initial: current, label: t("chat.rename") }, confirmLabel: t("common.save") });
    if (v && v.trim() && v.trim() !== current) rename.mutate(v.trim());
  };
  const moveTo = useMutation({ mutationFn: (spaceId: string | null) => chatsApi.update(chatId, { spaceId }), onSuccess: () => { refresh(); void qc.invalidateQueries({ queryKey: ["chat-spaces"] }); } });
  const stopAll = useMutation({ mutationFn: () => Promise.all(running.map((c) => chatsApi.cancel(c.id))), onSuccess: refresh });
  // Picking one provider in the composer pins it: the choice (and that provider's model) is kept on the conversation,
  // so leaving and coming back keeps talking to it. "Everyone" unpins. An @mention still overrides one message.
  const pin = useMutation({ mutationFn: (accountId: string | null) => chatsApi.update(chatId, { agentAccountId: accountId }), onSuccess: refresh });
  const combine = useMutation({
    mutationFn: () => {
      const acc = all.find((a) => a.status === "connected" && a.provider === "claude") ?? all.find((a) => a.status === "connected");
      return chatsApi.combine(chatId, combineChat?.agentAccountId ?? acc!.id);
    },
    onSuccess: refresh,
  });
  const submit = () => { if (text.trim() && !busy && recipients.length && !send.isPending && !uploading) send.mutate(); };
  // "/" lists the MCP connections; the chosen one's shortcuts sit above the box
  const mcp = useMcpShortcuts(text, setText, () => textareaRef.current?.focus());

  if (multi.isError) return <div className="p-6"><Alert tone="danger">{toMsg(multi.error)}</Alert></div>;
  if (!multi.data) return <div className="grid h-full place-items-center"><Spinner /></div>;
  const projectId = multi.data.chat.projectId;
  const err = send.error ?? stopAll.error ?? combine.error ?? pin.error ?? moveTo.error;
  const failed = (send.data?.results ?? []).filter((r) => r.error);
  const lastPrompt = [...turns].reverse().find((x) => x.kind === "prompt");
  const canCombine = members.length > 1 && running.length === 0 && !!lastPrompt && lastPrompt.segments.filter((s) => s.finishedAt).length > 1 && turns.at(-1)?.kind !== "combine";

  return (
    <div className="relative flex h-full min-h-0 flex-col">
      {threads.map((c) => <MemberFeed key={c.id} chatId={c.id} onData={onData} />)}
      <header className="@container flex flex-wrap items-center gap-2 border-b border-border px-3 py-2 md:gap-3 md:px-4">
        <button type="button" onClick={() => void askRename()} disabled={rename.isPending} className="grid size-9 shrink-0 place-items-center rounded-lg border border-border text-fg-muted hover:text-fg" aria-label={t("chatMenu.rename")} title={t("chatMenu.rename")}><Pencil className="size-4" /></button>
        <div className="min-w-[180px] flex-1">
          <div className="flex min-w-0 items-center gap-2">
            <h2 className="truncate text-sm font-semibold">{multi.data.chat.title}</h2>
            <span className={cn("flex shrink-0 items-center gap-1.5 rounded-full border border-border px-2 py-0.5 text-[11px]", running.length ? "text-warn" : "text-accent")}><span className={cn("size-1.5 rounded-full", running.length ? "animate-pulse bg-warn" : "bg-accent")} />{running.length ? t("chat.working") : t("chat.ready")}</span>
          </div>
          <div className="flex min-w-0 items-center gap-1 text-[11px] text-fg-muted">
            {/* the providers' marks only; their names stay in the tooltip */}
            {members.map((m) => { const a = accountOf(m, all); return a ? <span key={m.id} title={providerName(m, all)} aria-label={providerName(m, all)} className="shrink-0"><ProviderIcon provider={a.provider} size="xs" /></span> : null; })}
            <span className="mx-1 text-border">|</span>
            <FolderOpen className="size-3 shrink-0" />
            <select aria-label={t("spaces.moveTo")} value={multi.data.chat.spaceId ?? ""} disabled={moveTo.isPending} onChange={(e) => moveTo.mutate(e.target.value || null)} className="max-w-[10rem] truncate bg-transparent text-[11px] text-fg-muted hover:text-fg focus:outline-none">
              <option value="">{t("spaces.none")}</option>
              {(spaces.data ?? []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
        </div>
        {tools}
      </header>
      {temporary && (
        <div className="flex items-center gap-2 border-b border-border bg-surface-2 px-3 py-1.5 text-xs text-fg-muted md:px-4">
          <MessageSquareDashed className="size-3.5 shrink-0" />
          <span>{t("temporary.banner")}</span>
        </div>
      )}
      <WorkflowRunBar chatId={chatId} activity={members.map((m) => `${m.status}:${feeds[m.id]?.events.length ?? 0}`).join()} />

      <div data-chat-scroll className="relative min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-x-none px-4 pb-44 pt-3">
        <div className="mx-auto min-w-0 max-w-3xl space-y-3">
          {turns.map((turn) => (
            <div key={turn.key} className="space-y-3">
              {turn.kind === "prompt" ? (
                <>
                  <MessageList events={[turn.prompt]} partial="" running={false} projectId={projectId} />
                  {turn.recipients.length < members.length && <div className="text-end text-[11px] text-fg-muted">{t("providers.sentTo", { names: turn.recipients.map((r) => providerName(r, all)).join("، ") })}</div>}
                </>
              ) : (
                <div className="flex items-center gap-2 text-[11px] font-medium text-accent"><Combine className="size-3.5" /> {t("multi.combined")}<span className="h-px flex-1 bg-border" /></div>
              )}
              {turn.segments.map((seg) => {
                const streaming = seg.last && seg.member.status === "running";
                // id: a file's place in the conversation (a space's files open it here)
                return <div key={`${seg.member.id}:${seg.prompt.seq}`} id={`seg-${seg.member.id}-${seg.prompt.seq}`} className="scroll-mt-4 rounded-lg"><MessageList events={seg.events} partial={streaming ? (feeds[seg.member.id]?.partial ?? "") : ""} running={streaming} projectId={projectId} /></div>;
              })}
            </div>
          ))}
          {canCombine && (
            <div className="flex justify-center">
              <Button size="sm" variant="secondary" loading={combine.isPending} onClick={() => combine.mutate()} title={t("multi.combineHint")}><Combine className="size-4" /> {t("multi.combine")}</Button>
            </div>
          )}
        </div>
      </div>

      {/* ---- floating composer, like the chat page ---- */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-bg via-bg/95 to-transparent px-2 pb-2 pt-8 md:px-4 md:pb-3">
        <div className="pointer-events-auto mx-auto max-w-3xl">
          {err && <Alert tone="danger" className="mb-2">{toMsg(err)}</Alert>}
          {failed.length > 0 && <Alert tone="warn" className="mb-2">{failed.map((f) => `${providerName(members.find((m) => m.id === f.chatId) ?? members[0]!, all)}: ${f.error}`).join(" · ")}</Alert>}
          <form className="relative rounded-2xl border border-border bg-surface shadow-[0_12px_40px_-12px_rgba(0,0,0,0.6)] focus-within:border-accent/60" onSubmit={(e) => { e.preventDefault(); submit(); }} onDragOver={(e) => e.preventDefault()} onDrop={(e) => { if (e.dataTransfer.files.length) { e.preventDefault(); void addFiles(e.dataTransfer.files); } }}>
            <input ref={fileInput} type="file" multiple className="hidden" onChange={(e) => { if (e.target.files) void addFiles(e.target.files); e.target.value = ""; }} />
            {mcp.menu}
            {mcp.bar}
            {(attachments.length > 0 || uploading > 0) && (
              <ul className="flex flex-wrap gap-1.5 px-3 pt-3">
                {attachments.map((a) => (
                  <li key={a.path} className="flex items-center gap-1.5 rounded-md border border-border bg-surface-2 ps-1 pe-1.5 py-1 text-xs">
                    {a.mime.startsWith("image/") ? <button type="button" className="cursor-zoom-in" aria-label={a.name} onClick={() => viewImages([{ src: filesApi.rawUrl(projectId, a.path), name: a.name }])}><img src={filesApi.rawUrl(projectId, a.path)} alt="" className="size-6 rounded object-cover" /></button> : null}
                    <span className="max-w-40 truncate" dir="ltr">{a.name}</span>
                    <button type="button" className="text-fg-muted hover:text-danger" aria-label={t("common.remove")} onClick={() => setAttachments((v) => v.filter((x) => x.path !== a.path))}><X className="size-3.5" /></button>
                  </li>
                ))}
                {uploading > 0 && <li className="flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs text-fg-muted"><Spinner className="size-3" /> {t("common.uploading")}</li>}
              </ul>
            )}
            {suggestions.length > 0 && (
              <ul className="absolute bottom-full start-3 z-20 mb-1 w-[min(18rem,calc(100vw-2rem))] rounded-md border border-border bg-surface py-1 text-sm shadow-lg">
                {suggestions.map((m) => { const a = accountOf(m, all); return (
                  <li key={m.id}>
                    <button type="button" className="flex w-full items-center gap-2 px-3 py-1.5 text-start hover:bg-surface-2" onMouseDown={(e) => { e.preventDefault(); insertMention(handles.get(m.id)!); }}>
                      {a && <ProviderIcon provider={a.provider} size="xs" />}
                      <span className="truncate">{providerName(m, all)}</span>
                      <span dir="ltr" className="ms-auto font-mono text-xs text-fg-muted">@{handles.get(m.id)}</span>
                    </button>
                  </li>
                ); })}
              </ul>
            )}
            <textarea
              ref={textareaRef}
              dir="auto"
              className="max-h-60 min-h-[52px] w-full resize-none bg-transparent px-4 pt-3.5 pb-1 text-[15px] focus:outline-none"
              placeholder={recipients.length === members.length && members.length > 1 ? t("multi.placeholderAll") : t("multi.placeholderTo", { names: recipients.map((m) => providerName(m, all)).join("، ") })}
              value={text}
              rows={1}
              onChange={(e) => { setText(e.target.value); const m = /(^|\s)@([a-z0-9_]*)$/i.exec(e.target.value); setMentionQ(m ? m[2]!.toLowerCase() : null); }}
              onPaste={(e) => { const files = [...e.clipboardData.files]; if (files.length) { e.preventDefault(); void addFiles(files); } }}
              onKeyDown={(e) => { if (mcp.onKeyDown(e)) return; if (e.key === "Escape") { setMentionQ(null); setPicker(false); } if (e.key === "Enter" && suggestions.length > 0 && !e.metaKey && !e.ctrlKey) { e.preventDefault(); insertMention(handles.get(suggestions[0]!.id)!); return; } if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing && !softKeyboardOpen()) { e.preventDefault(); submit(); } }}
            />
            <div className="flex items-center gap-1.5 px-2.5 pb-2.5">
              <button type="button" className="grid size-9 place-items-center rounded-md border border-border text-fg-muted hover:bg-surface-2 hover:text-fg md:size-8" title={t("chat.attachHint")} aria-label={t("chat.attach")} onClick={() => fileInput.current?.click()}><Plus className="size-4" /></button>
              <TemplatesButton spaceId={multi.data?.chat.spaceId ?? null} onInsert={(x) => { setText((v) => (v.trim() ? `${v.replace(/\s*$/, "")}\n\n${x}` : x)); textareaRef.current?.focus(); }} />
              <div className="relative flex min-w-0 flex-1 items-center gap-1.5">
                <button type="button" className={cn("flex h-9 w-full min-w-0 items-center gap-2 rounded-md border px-2.5 text-start text-xs md:h-8 md:px-3", pinnedMember && !mentioned.length ? "border-accent/50 bg-accent-soft/20 hover:bg-accent-soft/30" : "border-border bg-surface-2/40 hover:bg-surface-2", picker && "border-accent/60")} onClick={() => setPicker(!picker)} title={pinnedMember ? t("providers.pinnedHint", { name: providerName(pinnedMember, all) }) : t("providers.to")}>
                  <span className="shrink-0 text-fg-muted">{t("providers.to")}:</span>
                  {recipients.length === members.length && members.length > 1 ? (
                    <><Users className="size-3.5 shrink-0 text-accent" /><span className="truncate">{t("providers.everyone")}</span></>
                  ) : (
                    <span className="flex min-w-0 items-center gap-1">{recipients.map((m) => { const a = accountOf(m, all); return a ? <ProviderIcon key={m.id} provider={a.provider} size="xs" /> : null; })}<span className="truncate">{recipients.map((m) => providerName(m, all)).join("، ")}</span></span>
                  )}
                  {pinnedMember && !mentioned.length && <ActivityChip state={activityOf(pinnedMember)} />}
                  {pin.isPending ? <Spinner className="ms-auto size-3.5" /> : <ChevronDown className="ms-auto size-3.5 shrink-0 text-fg-muted" />}
                </button>
                {/* the pinned provider's model, kept on its own conversation */}
                {pinnedMember && !mentioned.length && <MemberModel chat={pinnedMember} account={accountOf(pinnedMember, all)} onChanged={refresh} className="h-9 md:h-8" />}
                {picker && (
                  <ul className="absolute bottom-full start-0 z-20 mb-1 w-full min-w-[14rem] rounded-xl border border-border bg-surface p-1 text-sm shadow-xl">
                    {members.length > 1 && <li><button type="button" className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-start hover:bg-surface-2" onClick={() => { setTo(null); setPicker(false); if (pinnedMember) pin.mutate(null); }}><Users className="size-4 text-accent" /> <span className="flex-1">{t("providers.everyone")}</span>{!pinnedMember && <Check className="size-4 text-accent" />}</button></li>}
                    {members.map((m) => { const a = accountOf(m, all); return (
                      <li key={m.id}><button type="button" className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-start hover:bg-surface-2" onClick={() => { setTo(null); setPicker(false); if (pinnedMember?.id !== m.id) pin.mutate(m.agentAccountId); }}>{a && <ProviderIcon provider={a.provider} size="xs" />}<span className="flex-1 truncate">{providerName(m, all)}</span><span dir="ltr" className="font-mono text-[11px] text-fg-muted">@{handles.get(m.id)}</span>{pinnedMember?.id === m.id && <Check className="size-4 text-accent" />}</button></li>
                    ); })}
                  </ul>
                )}
              </div>
              {running.length > 0 && <Button type="button" variant="danger" className="size-9 shrink-0 rounded-full p-0" onClick={() => stopAll.mutate()} loading={stopAll.isPending} aria-label={t("multi.stopAll")} title={t("multi.stopAll")}><Square className="size-4" /></Button>}
              <Button type="submit" className="size-9 shrink-0 rounded-full p-0" disabled={!text.trim() || busy || uploading > 0} loading={send.isPending} aria-label={t("chat.send")}><ArrowUp className="size-4" /></Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
